import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { telegramAPI } from '@/api/endpoints/telegram.js';
import styles from '@/styles/pages/telegram/TelegramLogs.module.css';

const TelegramLogs = () => {
  const { uuid: projectUUID } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastId, setLastId] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const observerRef = useRef();
  const lastMessageRef = useRef();
  const loadingRef = useRef(false);

  useEffect(() => {
      document.title = `Сообщения телеграмма | DevSpace`
  }, []);

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} д назад`;

    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Загрузка сообщений
  const loadMessages = useCallback(async (isInitial = false) => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const currentLastId = isInitial ? null : lastId;
      const response = await telegramAPI.get(projectUUID, currentLastId, false);

      if (response.success && response.data) {
        const { items, last_id, has_more } = response.data;

        if (isInitial) {
          setMessages(items || []);
        } else {
          setMessages(prev => [...prev, ...(items || [])]);
        }

        setLastId(last_id);
        setHasMore(has_more);
        setError(null);
      } else {
        setError('Ошибка загрузки сообщений');
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Не удалось загрузить сообщения');
    } finally {
      setLoading(false);
      loadingRef.current = false;
      if (isInitial) {
        setInitialLoading(false);
      }
    }
  }, [projectUUID, lastId]);

  // Начальная загрузка
  useEffect(() => {
    loadMessages(true);
  }, []);

  // Intersection Observer для бесконечной прокрутки
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!lastMessageRef.current || !hasMore || loadingRef.current) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingRef.current && !initialLoading) {
          loadMessages(false);
        }
      },
      { threshold: 0.1 }
    );

    if (lastMessageRef.current) {
      observerRef.current.observe(lastMessageRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [messages, hasMore, initialLoading, loadMessages]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    setMessages([]);
    setLastId(null);
    setHasMore(true);
    loadMessages(true);
  };

  if (initialLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.bgGradient}></div>
        <div className={styles.bgGrid}></div>
        <div className={styles.loadingContainer}>
          <LoadingOutlined className={styles.loadingSpinner} />
          <p>Загрузка сообщений...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGradient}></div>
      <div className={styles.bgGrid}></div>

      <div className={styles.content}>
        {/* Шапка */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowLeftOutlined /> Назад
          </button>
          <h1 className={styles.title}>
            <MessageOutlined /> Логи Telegram
          </h1>
          <button className={styles.refreshButton} onClick={handleRefresh}>
            Обновить
          </button>
        </div>

        {/* Статистика */}
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <span className={styles.statValue}>{messages.length}</span>
            <span className={styles.statLabel}>Всего сообщений</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>
              {messages.filter(m => m.success).length}
            </span>
            <span className={styles.statLabel}>Успешных</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statValue}>
              {messages.filter(m => !m.success).length}
            </span>
            <span className={styles.statLabel}>Ошибок</span>
          </div>
        </div>

        {/* Список сообщений */}
        <div className={styles.messagesContainer}>
          {error && (
            <div className={styles.errorMessage}>
              <CloseCircleOutlined />
              <span>{error}</span>
            </div>
          )}

          {messages.length === 0 && !error && (
            <div className={styles.emptyState}>
              <MessageOutlined className={styles.emptyIcon} />
              <h3>Нет сообщений</h3>
              <p>Здесь будут отображаться сообщения из Telegram</p>
            </div>
          )}

          <div className={styles.messagesList} style={{ maxHeight: 'calc(100vh - 200px)' }}>
            {messages.map((message, index) => (
              <div
                key={message.id}
                ref={index === messages.length - 1 ? lastMessageRef : null}
                className={`${styles.messageCard} ${message.success ? styles.successMessage : styles.errorMessageCard}`}
              >
                <div className={styles.messageHeader}>
                  <div className={styles.messageStatus}>
                    {message.success ? (
                      <CheckCircleOutlined className={styles.successIcon} />
                    ) : (
                      <CloseCircleOutlined className={styles.errorIcon} />
                    )}
                    <span className={styles.messageId}>ID: {message.id}</span>
                  </div>
                  <div className={styles.messageMeta}>
                    <span className={styles.messageIp}>
                      <EnvironmentOutlined /> {message.ip}
                    </span>
                    <span className={styles.messageTime}>
                      <ClockCircleOutlined /> {formatDate(message.created_at)}
                    </span>
                  </div>
                </div>

                <div className={styles.messageText}>
                  {message.text || <span className={styles.emptyText}>(пустое сообщение)</span>}
                </div>

                <div className={styles.messageFooter}>
                  <span className={styles.messageDate}>
                    {new Date(message.created_at).toLocaleString('ru-RU')}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Индикатор загрузки */}
          {loading && (
            <div className={styles.loadingMore}>
              <LoadingOutlined className={styles.loadingMoreIcon} />
              <span>Загрузка сообщений...</span>
            </div>
          )}

          {/* Конец списка */}
          {!hasMore && messages.length > 0 && (
            <div className={styles.endMessage}>
              <span>Вы просмотрели все сообщения</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelegramLogs;